import { useCallback, useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import { sendBanKDetailToWhatsApp } from "../../../utils";
import {
  copyCanvasAsPng,
  downloadCanvasAsPng,
  getQrCanvasFromElement,
} from "../../../utils/qrCodeCanvas";

const COPY_FEEDBACK_MS = 2500;

const useQrCodeActions = ({
  qrCodeRef,
  analyticsPrefix,
  showSnackbar,
  bankDetails,
}) => {
  const [isQrImageCopied, setIsQrImageCopied] = useState(false);
  const [isIbanCopied, setIsIbanCopied] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const copyTimeoutRef = useRef(null);
  const ibanCopyTimeoutRef = useRef(null);

  const { bankName, accountTitle, accountNumber, iban,qr_share_key } = bankDetails;

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      if (ibanCopyTimeoutRef.current) {
        clearTimeout(ibanCopyTimeoutRef.current);
      }
    },
    [],
  );

  const markQrImageCopied = useCallback(() => {
    setIsQrImageCopied(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => {
      setIsQrImageCopied(false);
    }, COPY_FEEDBACK_MS);
  }, []);

  const resolveCanvas = useCallback(async () => {
    try {
      return await getQrCanvasFromElement(qrCodeRef.current);
    } catch {
      if (!qrCodeRef.current) {
        throw new Error("QR element not found");
      }
      return html2canvas(qrCodeRef.current);
    }
  }, [qrCodeRef]);

  const handleCopyQrImage = useCallback(async () => {
    triggerLogEvent(analytics, `${analyticsPrefix}_copy_image`);
    try {
      const canvas = await resolveCanvas();
      await copyCanvasAsPng(canvas);
      markQrImageCopied();
    } catch {
      showSnackbar({
        message: "Failed to copy",
        severity: "error",
      });
    }
  }, [analyticsPrefix, markQrImageCopied, resolveCanvas, showSnackbar]);

  const handleDownloadQrCode = useCallback(async () => {
    setIsDownloadLoading(true);
    triggerLogEvent(analytics, `${analyticsPrefix}_download`);
    try {
      const canvas = await resolveCanvas();
      downloadCanvasAsPng(canvas);
    } catch {
      // silent fallback failure matches previous behaviour
    } finally {
      setIsDownloadLoading(false);
    }
  }, [analyticsPrefix, resolveCanvas]);

  const handlePrintQrCode = useCallback(() => {
    triggerLogEvent(analytics, `${analyticsPrefix}_print`);
    const printWindow = window.open("", "", "width=600,height=600");
    const qrCodeElement = qrCodeRef.current;
    if (!printWindow || !qrCodeElement) return;

    printWindow.document.write(
      "<html><head><title>Rupin QR Code</title></head><body>",
    );
    printWindow.document.write('<div style="text-align: center;">');
    printWindow.document.write(qrCodeElement.outerHTML);
    printWindow.document.write("</div>");
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }, [analyticsPrefix, qrCodeRef]);

  const handleCopyIban = useCallback(async () => {
    if (!iban) return;
    try {
      await navigator.clipboard.writeText(iban);
      setIsIbanCopied(true);
      if (ibanCopyTimeoutRef.current) {
        clearTimeout(ibanCopyTimeoutRef.current);
      }
      ibanCopyTimeoutRef.current = setTimeout(() => {
        setIsIbanCopied(false);
      }, COPY_FEEDBACK_MS);
      triggerLogEvent(analytics, `${analyticsPrefix}_copy_iban`);
    } catch (error) {
      console.error(error);
    }
  }, [analyticsPrefix, iban]);

  const handleShareDetails = useCallback(() => {
    triggerLogEvent(analytics, `${analyticsPrefix}_share_details`);
    sendBanKDetailToWhatsApp({
      bankName,
      accountTitle,
      accountNumber,
      iban,
      qr_share_key
    });
  }, [analyticsPrefix, accountNumber, accountTitle, bankName, iban]);

  return {
    isQrImageCopied,
    isIbanCopied,
    isDownloadLoading,
    handleCopyQrImage,
    handleDownloadQrCode,
    handlePrintQrCode,
    handleCopyIban,
    handleShareDetails,
  };
};

export default useQrCodeActions;
